import { useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, Input, Modal } from "@shop-ledger/ui-components";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/apiClient";
import { topUpStock } from "../lib/topUpStock";
import { SupplierSelect } from "./SupplierSelect";
import { StatusBanner } from "./StatusBanner";

export interface TopUpStockModalProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  branchId: string;
  onToppedUp: () => void;
}

/**
 * "Top Up Stock" quick action (business-owner walkthrough follow-up) — no dedicated
 * backend endpoint exists for a direct stock increase, so this composes the two existing,
 * already-tested purchase-order calls instead of adding a second way to mutate
 * `current_stock`: `POST /purchase-orders` (single line, `supplier_id` optional — the
 * schema already allows null) immediately followed by `POST /purchase-orders/{id}/receive`
 * for that same line/quantity. If the tenant has an approval rule configured that routes
 * new POs to PENDING_APPROVAL, the receive call 409s — that's surfaced as a real message
 * rather than silently failing, since in that case stock genuinely hasn't moved yet.
 */
export function TopUpStockModal({ open, onClose, itemId, itemName, branchId, onToppedUp }: TopUpStockModalProps) {
  const { ensureFreshToken } = useAuth();
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("0.00");
  const [supplierId, setSupplierId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await topUpStock(token, {
        branchId,
        itemId,
        quantity: asDecimalString(quantity),
        unitCost: asDecimalString(unitCost),
        supplierId,
        batchNumber,
        expiryDate,
      });
      setQuantity("");
      setUnitCost("0.00");
      setSupplierId("");
      setBatchNumber("");
      setExpiryDate("");
      onToppedUp();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          "This tenant requires approval before new purchase orders are received. The order was created but stock hasn't " +
            "increased yet — find it under Purchase Orders once it's approved.",
        );
      } else {
        setError(err instanceof ApiError ? err.message : "Could not top up stock.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Top up stock — ${itemName}`}>
      <p className="muted">Creates and immediately receives a purchase order for this quantity — the same effect as ordering and receiving stock, in one step.</p>
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <Input label="Quantity to add" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          <Input label="Unit cost (optional)" inputMode="decimal" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
          <SupplierSelect id="topup-supplier" label="Supplier (optional)" value={supplierId} onChange={setSupplierId} />
          <Input
            label="Batch number (optional)"
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder="e.g. LOT-2026-07"
          />
          <Input label="Expiry date (optional)" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
        </div>
        {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}
        <div className="form-actions">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Adding stock…" : "Add stock"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
